import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GPTResponse, POSDetail, PaymentRequest, PaymentResponse, PaymentRecordRequest, PaymentRecordResponse, FlowRecommendationResponse, FlowUpsellingResponse, FlowCrossSellingResponse, CategoryFilter } from './api.dto';
import { v4 as uuidv4 } from 'uuid';
import { Product } from '../components/products/products.component';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  public details?: POSDetail;
  private sessID: string = '';
  private lastQuery: string = '';
  public integratedRecommendationsFlow: boolean = false;

  constructor(private http: HttpClient) { }

  makeFirstSearch(query: string, datasetID: string, catalogID: string, pos: string) {
    return this.http.post<GPTResponse>(`https://n8n.tercerpiso.net/webhook/33fa35a8-b6d5-40aa-8496-c9eb07f36318`, {
      query,
      datasetID,
      catalogID,
      pos,
      sessID: this.sessID
    });
  }

  makeFirstSearchV2(
    query: string, 
    datasetID: string, 
    catalog_id: string, 
    posID: string,
    confirmation?: { todoId: string; productId: string } | null,
    detailResponse?: { todoId: string; detail: string } | null
  ) {
    this.lastQuery = query;
    const body: any = {
      query,
      catalog_id,
      posID,
      sessID: this.sessID
    };
    
    if (confirmation) {
      body.confirmation = confirmation;
    }
    
    if (detailResponse) {
      body.detailResponse = detailResponse;
    }
    
    return this.http.post<FlowRecommendationResponse>(`${environment.apiUrl}/flow/recommendation`, body);
  }

  getUpsellingProducts(catalog_id: string, posID: string, product_id: string) {
    return this.http.post<FlowUpsellingResponse>(`${environment.apiUrl}/flow/upselling`, {
      originalQuery: this.lastQuery,
      catalog_id,
      posID,
      sessID: this.sessID,
      product_id
    });
  }

  getCrossSellingProducts(catalog_id: string, posID: string, product_id: string, categories?: CategoryFilter[]) {
    const body: any = {
      originalQuery: this.lastQuery,
      catalog_id,
      posID,
      sessID: this.sessID,
      product_id
    };

    // Enviar categorías al endpoint de cross-selling si están disponibles
    if (categories && categories.length > 0) {
      // Enviar explícitamente field y values para cada categoría
      body.categories = categories.map(c => ({
        field: c.field,
        values: Array.isArray(c.values) ? c.values : []
      }));
    }

    // Log para depurar cómo se envían las categorías al cross-selling
    // console.log('[ApiService] Enviando cross-selling con body:', body);

    return this.http.post<FlowCrossSellingResponse>(`${environment.apiUrl}/flow/crossselling`, body);
  }

  getCrossSellingProductsBatch(
    catalog_id: string, 
    posID: string, 
    productIds: string[], 
    categories?: CategoryFilter[]
  ) {
    const body: any = {
      products: productIds.map(id => ({ product_id: id })),
      originalQuery: this.lastQuery,
      sessID: this.sessID,
      catalog_id,
      posID
    };

    // Enviar categorías al endpoint de cross-selling si están disponibles
    if (categories && categories.length > 0) {
      body.categories = categories.map(c => ({
        field: c.field,
        values: Array.isArray(c.values) ? c.values : []
      }));
    }

    // Log para depurar cómo se envían los datos al cross-selling
    // console.log('[ApiService] Enviando cross-selling batch con body:', body);

    return this.http.post<FlowCrossSellingResponse>(`${environment.apiUrl}/flow/crossselling`, body);
  }

  getPosData(posID: string) {
    return this.http.get<POSDetail>(`${environment.apiUrl}/point-of-sales/${posID}`);
  }

  resetConversation() {
    this.sessID = uuidv4();
    this.integratedRecommendationsFlow = false;
  }

  getProduct(productID: string) {
    return this.http.get<Product>(`https://n8n.tercerpiso.net/webhook/b7de6fe1-2618-4c22-8559-5bdc8b90e554?catalog=${this.details!.catalog.table_name}&id=${productID}`);
  }

  createPayment(paymentRequest: PaymentRequest) {
    return this.http.post<PaymentResponse>(`${environment.paxarelUrl}/payment/link`, paymentRequest);
  }

  recordPayment(paymentRecord: PaymentRecordRequest): Observable<PaymentRecordResponse> {
    return this.http.post<PaymentRecordResponse>(`${environment.apiUrl}/payments`, paymentRecord);
  }

  getPaxarelPaymentStatus(transactionId: string): Observable<any> {
    return this.http.get<any>(`${environment.paxarelUrl}/payment/status/${transactionId}`);
  }
}
